import React, { BaseSyntheticEvent } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Field,
  FieldProps,
  useField,
} from 'formik';
import classNames from 'classnames';

import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { RecipientDeliveryTypes } from 'hellospa/page/prep-and-send/data/types';
import * as types from 'hellospa/page/prep-and-send/data/types';
import TextField from 'hellospa/foundation/control/text-field';
import { ChoiceChip } from 'dig-components/chips';
import ErrorMessage from 'hellospa/components/error-message';
import ContactInput from 'hellospa/components/contact-input';
import { ArrowDirection, TooltipPoint } from '../../../foundation/control/tooltip';
import styles from './index.scss';
import messages from './intl';

type Props = {
  index: number,
  recipient: types.Signer
};

const Signer = ({ index, recipient }: Props) => {
  const intl = useIntl();
  const showRole = useSelector(selectors.allowSignerRole);
  const isEmbedded = useSelector(selectors.isEmbedded);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);
  const showInlinePreview = useSelector(selectors.showInlinePreview);
  const isSmsToolsOn = useSelector(selectors.isSmsToolsOn);
  const canUpgradeSmsSignatureDeliveryTier = useSelector(selectors.canUpgradeSmsSignatureTier);

  const [recipientsField] = useField<types.Signer[]>('recipients');
  const parent = `recipients.${index}`;

  const [, nameMeta] = useField<String>(`${parent}.name`);
  const [, emailMeta] = useField<String>(`${parent}.email`);
  const [, smsDeliveryMeta, smsDeliveryHelpers] = useField<String>(`${parent}.smsDeliveryMobileNumber`);
  const [deliveryTypeField, , deliveryTypeHelpers] = useField<String>(`${parent}.deliveryType`);
  const [recipientReassigmentField] = useField('recipientReassignment');
  const [, authTypeMeta] = useField<String>(`${parent}.authType`);

  const MAX_SMS_DELIVERIES = 5;

  const numOfSmsDeliveries = React.useMemo(() => {
    return recipientsField.value.reduce((acc: number, r: types.Signer) => {
      if (r.deliveryType === RecipientDeliveryTypes.EmailAndSms) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [recipientsField.value]);

  const smsDeliveryDisabledTooltip = React.useMemo(() => {
    if (recipientReassigmentField.value) {
      return intl.formatMessage(messages.smsDeliveryWithReassignTooltip);
    } else if (authTypeMeta.value === 'smsAuthNumber') {
      return intl.formatMessage(messages.smsDeliveryWithSmsAuthTooltip);
    } else if (numOfSmsDeliveries === MAX_SMS_DELIVERIES
      && deliveryTypeField.value === RecipientDeliveryTypes.Email) {
      return intl.formatMessage(messages.maxSmsDeliveryTooltip,
        { maxSmsDeliveries: MAX_SMS_DELIVERIES });
    } else if (!canUpgradeSmsSignatureDeliveryTier) {
      return intl.formatMessage(messages.unableToAutoUpgradeSmsDelivery);
    }
    return null;
  }, [
    recipientReassigmentField.value,
    authTypeMeta.value,
    numOfSmsDeliveries,
    deliveryTypeField.value,
    intl,
    canUpgradeSmsSignatureDeliveryTier,
  ]);

  return (
    <div
      className={styles.fields}
      data-qa-ref={`${parent}.fields`}
    >
      <div className={styles.fieldRow}>
        {showRole && recipient.role && (
          <div>
            <h4 data-qa-ref={`${parent}.role`}>{recipient.role.name}</h4>
          </div>
        )}
        {!isDeepIntegration && !recipient.role && (
          <div>
            <h4
              id={`${parent}.signerHeader`}
              data-qa-ref={`${parent}.signer`}
            >
              {nameMeta.value
                ? nameMeta.value
                : intl.formatMessage(messages.signerType)}
            </h4>
          </div>
        )}
      </div>

      <div
        className={classNames(styles.fieldRow, {
          [styles.fullWidthFieldRow]: showInlinePreview,
        })}
      >
        <div
          className={classNames({
            [styles.errorFieldContainer]: nameMeta.touched && nameMeta.error,
          })}
        >
          <label htmlFor={`${parent}.name`}>
            {intl.formatMessage(messages.fieldNameLabel)}
          </label>
          <Field
            name={`${parent}.name`}
          >
            {({ field }: FieldProps) => <TextField
              className={classNames(styles.recipient)}
              id={`${parent}.name`}
              autoFocus={index === 0}
              data-qa-ref={`${parent}.fields.name`}
              placeholder={intl.formatMessage(messages.fieldNamePlaceholder)}
              {...field} />}
          </Field>
          <div
            className={styles.errorContainer}
            role="name-error"
            data-qa-ref={`${parent}.errors.name`}
          >
            <ErrorMessage
              className={styles.error}
              name={`${parent}.name`} />
          </div>
        </div>
        {/** Add DIG ChoiceChip for email and email + sms delivery if SMS tools add-on is on */}
        {isSmsToolsOn && (
          <div className={styles.deliveryTypeChoiceChipsContainer}>
            <div>Delivery method</div>
            <div className={styles.deliveryTypeChoiceChips}>
              <ChoiceChip
                isSelected={deliveryTypeField.value === RecipientDeliveryTypes.Email}
                className={classNames(deliveryTypeField.value === RecipientDeliveryTypes.Email
                  && styles.choiceChipIsSelected)}
                onClick={(e: BaseSyntheticEvent) => {
                  e.preventDefault();
                  deliveryTypeHelpers.setValue(RecipientDeliveryTypes.Email);
                  smsDeliveryHelpers.setValue('');
                }}
              >
                {intl.formatMessage(messages.prepAndSendDeliveryTypeEmail)}
              </ChoiceChip>
              <label className={styles.smsDeliveryToggleLabel}>
                {
                  (smsDeliveryDisabledTooltip)
                  && <TooltipPoint
                    className={styles.tooltipDownSmsDelivery}
                    arrow={ArrowDirection.Bottom}
                  >
                    {smsDeliveryDisabledTooltip}
                  </TooltipPoint>
                }
                <ChoiceChip
                  disabled={!!smsDeliveryDisabledTooltip}
                  isSelected={deliveryTypeField.value === RecipientDeliveryTypes.EmailAndSms}
                  className={classNames(
                    deliveryTypeField.value === RecipientDeliveryTypes.EmailAndSms
                    && styles.choiceChipIsSelected,
                  )}
                  onClick={(e: BaseSyntheticEvent) => {
                    e.preventDefault();
                    deliveryTypeHelpers.setValue(RecipientDeliveryTypes.EmailAndSms);
                  }}
                >
                  {intl.formatMessage(messages.prepAndSendDeliveryTypeEmailSms)}
                </ChoiceChip>
              </label>
            </div>
          </div>
        )}

        {/** If SMS tools isn't enabled, render email on same row as name field */}
        {!isSmsToolsOn && (
        <div
          className={classNames({
            [styles.errorFieldContainer]: emailMeta.touched && emailMeta.error,
          })}
        >
          <label htmlFor={`${parent}.email`}>
            {intl.formatMessage(messages.fieldEmailLabel)}
          </label>
          <Field name={`${parent}.email`}>
            {({ field, form }: FieldProps) => (
              <ContactInput
                id={`${parent}.email`}
                data-qa-ref={`${parent}.fields.email`}
                placeholder={intl.formatMessage(messages.fieldEmailPlaceholder)}
                className={styles.dropdownField}
                onSelectContact={(contact: types.Contact) => {
                  form.setFieldValue(`${parent}.name`, contact.name);
                  form.setFieldValue(field.name, contact.value);
                  form.validateField(`${parent}.name`);
                  form.validateField(field.name);
                }}
                contactTypes={['E']}
                searchEnabled={!isEmbedded || isDeepIntegration}
                {...field}
                onChange={(value: string) => form.setFieldValue(field.name, value)} />
            )}
          </Field>
          <div
            className={styles.errorContainer}
            role="email-error"
            data-qa-ref={`${parent}.errors.email`}
          >
            <ErrorMessage
              className={styles.error}
              name={`${parent}.email`} />
          </div>
        </div>)}
      </div>

      {/** If feature is turned on, add email and optional sms to the next row */}
      {isSmsToolsOn && (
        <div className={classNames(styles.fieldRow, {
          [styles.halfWidth]: deliveryTypeField.value !== RecipientDeliveryTypes.EmailAndSms,
        })}
        >
          <div
            className={classNames({
              [styles.errorFieldContainer]: emailMeta.touched && emailMeta.error,
            })}
          >
            <label htmlFor={`${parent}.email`}>
              {intl.formatMessage(messages.fieldEmailLabel)}
            </label>
            <Field name={`${parent}.email`}>
              {({ field, form }: FieldProps) => (
                <ContactInput
                  id={`${parent}.email`}
                  data-qa-ref={`${parent}.fields.email`}
                  placeholder={intl.formatMessage(messages.fieldEmailPlaceholder)}
                  className={styles.dropdownField}
                  onSelectContact={(contact: types.Contact) => {
                    form.setFieldValue(`${parent}.name`, contact.name);
                    form.setFieldValue(field.name, contact.value);
                    form.validateField(`${parent}.name`);
                    form.validateField(field.name);
                  }}
                  contactTypes={['E']}
                  searchEnabled={!isEmbedded || isDeepIntegration}
                  {...field}
                  onChange={(value: string) => form.setFieldValue(field.name, value)} />
              )}
            </Field>
            <div
              className={styles.errorContainer}
              role="email-error"
              data-qa-ref={`${parent}.errors.email`}
            >
              <ErrorMessage
                className={styles.error}
                name={`${parent}.email`} />
            </div>
          </div>

          <div
            className={classNames({
              [styles.errorFieldContainer]: smsDeliveryMeta.touched && smsDeliveryMeta.error,
              [styles.displayNone]: deliveryTypeField.value !== RecipientDeliveryTypes.EmailAndSms,
            })}
          >
            <label htmlFor={`${parent}.smsDeliveryMobileNumber`}>
              {intl.formatMessage(messages.recipientMobilePhoneNumberLabel)}
            </label>
            <Field name={`${parent}.smsDeliveryMobileNumber`}>
              {({ field, form }: FieldProps) => (
                <ContactInput
                  id={`${parent}.smsDeliveryMobileNumber`}
                  data-qa-ref={`${parent}.fields.smsDeliveryMobileNumber`}
                  placeholder={intl.formatMessage(messages.fieldMobilePhonePlaceholder)}
                  className={styles.dropdownField}
                  contactTypes={['F']}
                  onSelectContact={(contact: types.Contact) => {
                    form.setFieldValue(field.name, contact.value);
                  }}
                  isSmsInput
                  {...field}
                  onChange={(value: string) => form.setFieldValue(field.name, value)} />
              )}
            </Field>
            <div
              className={styles.errorContainer}
              role="phone-error"
              data-qa-ref={`${parent}.errors.smsDeliveryMobileNumber`}
            >
              <ErrorMessage
                className={styles.error}
                name={`${parent}.smsDeliveryMobileNumber`} />
            </div>
          </div>
        </div>)}
    </div>
  );
};

export default Signer;
